import { useState, useMemo } from 'react';
import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import type { Human } from '@mm-shared/types/Human';

/**
 * Sentinel value representing a "Create new human" option in the combobox.
 * When selected, onCreateNew is called with the current query text.
 */
const CREATE_NEW_SENTINEL = '__CREATE_NEW__';

type HumanOrCreateNew = Human | typeof CREATE_NEW_SENTINEL;

export type HumanComboboxProps = {
  /** All available humans to search from (typically from map3DataSlice.humans) */
  humans: Human[];
  /** Currently selected human (null if none) */
  selectedHuman: Human | null;
  /** Called when an existing human is selected */
  onSelect: (human: Human) => void;
  /** Called when user wants to create a new human with the given name */
  onCreateNew: (name: string) => void;
  /** Accessible label for the input */
  'aria-label'?: string;
  /** Whether the combobox should auto-focus */
  autoFocus?: boolean;
  /** Placeholder text */
  placeholder?: string;
  /** Called when the user cancels (Escape key) */
  onCancel?: () => void;
};

/**
 * HumanCombobox — a searchable combobox for selecting or creating a Human.
 *
 * Features:
 * - Filters humans[] by name as the user types
 * - Allows selecting an existing Human
 * - Shows a "Create new" option when no exact match exists
 * - Reusable by ObjectiveInfoTab (owner) and Map3Team
 *
 * Uses Headless UI Combobox with Catalyst-aligned styling.
 */
export function HumanCombobox({
  humans,
  selectedHuman,
  onSelect,
  onCreateNew,
  'aria-label': ariaLabel = 'Search for a person',
  autoFocus = false,
  placeholder = 'Search or create...',
  onCancel,
}: HumanComboboxProps) {
  const [query, setQuery] = useState('');

  const filteredHumans = useMemo(() => {
    if (!query.trim()) return humans;
    const lower = query.toLowerCase();
    return humans.filter((h) => h.name.toLowerCase().includes(lower));
  }, [humans, query]);

  // Show "Create new" option when query doesn't exactly match any existing human
  const showCreateOption = useMemo(() => {
    if (!query.trim()) return false;
    const lower = query.trim().toLowerCase();
    return !humans.some((h) => h.name.toLowerCase() === lower);
  }, [humans, query]);

  const handleChange = (value: HumanOrCreateNew | null) => {
    if (value === CREATE_NEW_SENTINEL) {
      onCreateNew(query.trim());
      setQuery('');
    } else if (value && typeof value === 'object') {
      onSelect(value);
      setQuery('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && onCancel) {
      e.preventDefault();
      onCancel();
    }
  };

  return (
    <Combobox<HumanOrCreateNew | null>
      value={selectedHuman}
      onChange={handleChange}
      onClose={() => setQuery('')}
    >
      <div className="relative">
        <div className="relative">
          <ComboboxInput
            aria-label={ariaLabel}
            autoFocus={autoFocus}
            placeholder={placeholder}
            displayValue={(human: HumanOrCreateNew | null) =>
              human && typeof human === 'object' ? human.name : ''
            }
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            className={clsx(
              'w-full rounded-lg border border-zinc-950/10 bg-white py-1.5 pl-3 pr-10 text-sm/6 text-zinc-950',
              'focus:outline-none focus:ring-2 focus:ring-blue-500',
              'dark:border-white/10 dark:bg-white/5 dark:text-white',
            )}
          />
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronUpDownIcon className="size-5 text-zinc-400" aria-hidden="true" />
          </span>
        </div>

        <ComboboxOptions
          anchor="bottom start"
          transition
          className={clsx(
            'w-[var(--input-width)] rounded-xl border border-zinc-950/5 bg-white p-1 empty:hidden',
            'shadow-lg ring-1 ring-zinc-950/10',
            'dark:bg-zinc-800 dark:ring-white/10',
            'transition-opacity duration-100 ease-in data-[closed]:opacity-0',
          )}
        >
          {filteredHumans.map((human) => (
            <ComboboxOption
              key={human.uuid}
              value={human}
              className={clsx(
                'group flex cursor-default select-none items-center gap-2 rounded-lg px-3 py-1.5',
                'text-sm/6 text-zinc-950 dark:text-white',
                'data-[focus]:bg-blue-500 data-[focus]:text-white',
              )}
            >
              <CheckIcon className="invisible size-4 group-data-[selected]:visible" aria-hidden="true" />
              <span>{human.name}</span>
              {human.email && (
                <span className="ml-auto text-xs text-zinc-500 group-data-[focus]:text-blue-100 dark:text-zinc-400">
                  {human.email}
                </span>
              )}
            </ComboboxOption>
          ))}

          {showCreateOption && (
            <ComboboxOption
              value={CREATE_NEW_SENTINEL}
              className={clsx(
                'group flex cursor-default select-none items-center gap-2 rounded-lg px-3 py-1.5',
                'text-sm/6 text-blue-600 dark:text-blue-400',
                'data-[focus]:bg-blue-500 data-[focus]:text-white',
              )}
            >
              <span className="size-4 flex items-center justify-center font-bold" aria-hidden="true">
                +
              </span>
              <span>
                Create &ldquo;{query.trim()}&rdquo;
              </span>
            </ComboboxOption>
          )}

          {filteredHumans.length === 0 && !showCreateOption && (
            <div className="px-3 py-2 text-sm text-zinc-500">No results found</div>
          )}
        </ComboboxOptions>
      </div>
    </Combobox>
  );
}
